import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Organization } from '../models/organization';
import { Paginator } from '../models/paginator';
import { User } from '../models/user';

/** Raw shape returned by GET /organizations/me/quotas. */
export interface Quotas {
  volume: number,
  trademarks: number,
  cases: number,
  /**
   * Crawler URLs are metered by a ledger, so the backend already returns a full
   * balance instead of a raw count — no need to recompose the limit client-side.
   */
  crawlerUrls: CrawlerUrlBalance
}

export interface CrawlerUrlBalance {
  period: number,
  subscribed: number,
  consumed: number,
  reserved: number,
  available: number,
  /** Always false: the exploration allowance is closed by default (0 = no exploration). */
  unlimited: boolean
}
export interface OrganizationQuotas {
  volume: {
    value: number,
    exceeded: boolean
  },
  trademarks: {
    value: number,
    exceeded: boolean
  },
  cases: {
    value: number,
    exceeded: boolean
  },
  crawlerUrls: {
    value: number,
    // the year's exploration allowance (plan ceiling + extras); 0 = exploration not enabled
    subscribed: number,
    // held by explorations running, and what is left for a new one
    reserved: number,
    available: number,
    exceeded: boolean
  },
  exceeded: number;
}


// The backend already resolved the allowance and the balance. An organisation without allowance has no
// exploration at all: that is not an exceeded quota.
function crawlerUrlsQuota(quotas: Quotas): OrganizationQuotas['crawlerUrls'] {
  const balance = quotas.crawlerUrls;
  return {
    value: balance?.consumed || 0,
    subscribed: balance?.subscribed || 0,
    reserved: balance?.reserved || 0,
    available: balance?.available || 0,
    exceeded: !!balance && balance.subscribed > 0 && balance.available <= 0,
  };
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {

  private organizationSubject: BehaviorSubject<Organization | undefined> = new BehaviorSubject<Organization | undefined>(undefined);
  public organization: Observable<Organization | undefined>;

  private quotasSubject: BehaviorSubject<OrganizationQuotas | undefined> = new BehaviorSubject<OrganizationQuotas | undefined>(undefined);
  public quotasObs: Observable<OrganizationQuotas | undefined>;
  public quotas: OrganizationQuotas = {
    volume: {
      value: 0,
      exceeded: false
    },
    trademarks: {
      value: 0,
      exceeded: false
    },
    cases: {
      value: 0,
      exceeded: false
    },
    crawlerUrls: {
      value: 0,
      subscribed: 0,
      reserved: 0,
      available: 0,
      exceeded: false
    },
    exceeded: 0
  }


  constructor(
    private http: HttpClient
  ) {
    this.organizationSubject.next(undefined);
    this.organization = this.organizationSubject.asObservable();
    this.quotasSubject.next(undefined);
    this.quotasObs = this.quotasSubject.asObservable();
    this.organization.subscribe(async (organization: Organization | undefined) => {
      if (organization)
        await this._setQuotas(organization);
    });
  }


  invite(user: {
    firstName?: string,
    lastName?: string,
    email?: string,
    role?: string
  }) {
    return firstValueFrom(this.http
      .post<User>(`${environment.backend}/admin/invite`, user));
  }

  removeFromOrganization(userId: string) {
    return firstValueFrom(this.http
      .delete<any>(`${environment.backend}/organizations/users/${userId}`));
  }

  mine(): Promise<Organization> {
    return firstValueFrom(this.http
      .get<Organization>(`${environment.backend}/organizations/me`).pipe(
        tap((res) => {
          this.organizationSubject.next(res);
        }),
        map((res) => {
          return res;
        })
      ));
  }

  myQuotas(): Promise<Quotas> {
    return firstValueFrom(this.http
      .get<Quotas>(`${environment.backend}/organizations/me/quotas`));
  }

  async _setQuotas(organization?: Organization) {
    const quotas = await this.myQuotas();

    if (organization && organization.subscription && organization.subscription.plan && organization.subscription.plan.maxVolume && quotas) {
      this.quotas = {
        volume: {
          value: quotas.volume / (1024 * 1024 * 1024),
          exceeded: quotas.volume / (1024 * 1024 * 1024) >= (organization.subscription.plan.maxVolume + (organization.subscription.additionalVolume || 0))
        },
        trademarks: {
          value: quotas.trademarks,
          exceeded: organization.subscription.plan.maxTrademarks ? quotas.trademarks >= (organization.subscription.plan.maxTrademarks + (organization.subscription.additionalTrademarks || 0)) : false
        },
        cases: {
          value: quotas.cases,
          exceeded: organization.subscription.plan.maxCases ? quotas.cases >= (organization.subscription.plan.maxCases + (organization.subscription.additionalCases || 0)) : false
        },
        crawlerUrls: crawlerUrlsQuota(quotas),
        exceeded: 0
      }
      this.quotas.exceeded = (this.quotas.volume.exceeded ? 1 : 0) + (this.quotas.trademarks.exceeded ? 1 : 0) + (this.quotas.cases.exceeded ? 1 : 0) + (this.quotas.crawlerUrls.exceeded ? 1 : 0)
    } else if (quotas) {
      // no volume ceiling, but exploration may still be enabled by the organisation's extras
      this.quotas = { ...this.quotas, crawlerUrls: crawlerUrlsQuota(quotas) };
    }
    this.quotasSubject.next(this.quotas);
  }

  uploadLogo(logo: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append("image", logo);
    return firstValueFrom(this.http.post<{ url: string }>(`${environment.backend}/upload/organizationLogo`, formData));
  }

  update(organization: Organization): Promise<Organization> {
    return firstValueFrom(this.http.put<Organization>(`${environment.backend}/organizations/me`, {
      name: organization.name,
      logo: organization.logo,
      headquartersAddress: organization.headquartersAddress
    }));
  }

  countUsers(): Promise<{ count: number }> {
    return firstValueFrom(this.http
      .get<{ count: number }>(`${environment.backend}/organizations/users/count`));
  }

  suspendUser(userId: string, isSuspended: boolean): Promise<User> {
    return firstValueFrom(this.http
      .put<User>(`${environment.backend}/organizations/users/${userId}/suspend`, { isSuspended }));
  }

  retrieveAllUsers(pageNumber: number, limit: number, offset: number, sort?: string, query?: string): Promise<Paginator<User>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset)

    if (sort) {
      params = params.set('sort', sort);
    }

    if (query) {
      params = params.set('q', query);
    }

    return firstValueFrom(this.http
      .get<Paginator<User>>(`${environment.backend}/organizations/users`, { params }));
  }

  retrieveUsersByRole(roles?: string[]): Promise<User[]> {
    let params = new HttpParams()

    if (roles) {
      roles.forEach((role) => {
        params = params.append('role[]', role);
      })
    }

    return firstValueFrom(this.http
      .get<User[]>(`${environment.backend}/organizations/roles`, { params }));
  }

  resendInvite(id: string) {
    return firstValueFrom(this.http
      .post<any>(`${environment.backend}/organizations/users/${id}/invite/resend`, {}));
  }

}
